import {
  Product,
  ProductShortName,
  SubscriptionGroupName,
  SubscriptionName,
  SubscriptionProductIds,
  SubscriptionType,
} from '@app/enums/_index';

import { LMSSubscription } from '@app/models/_index';
import { environment } from '@environments/environment';

/**
 * Get Subscription Name by subscription slug or name
 * @params subscription - subscription alias, slug or name
 * @returns subscription display name or transformed subscription name
 */
export function getSubscriptionName(subscription: string): string {
  switch (subscription) {
    case SubscriptionType.LEGACY_GENESYS_CLOUD_LEARNING_SUBSCRIPTION:
    case SubscriptionType.GENESYS_CLOUD_LEARNING_SUBSCRIPTION:
    case SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION:
      return SubscriptionName.GENESYS_CLOUD_SUBSCRIPTION;
    case SubscriptionType.GENESYS_ENGAGE_LEARNING_SUBSCRIPTION:
    case SubscriptionType.LEGACY_GENESYS_ENGAGE_LEARNING_SUBSCRIPTION:
    case SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION:
      return SubscriptionName.GENESYS_ENGAGE_SUBSCRIPTION;
    case SubscriptionType.LEGACY_PURECONNECT_LEARNING_SUBSCRIPTION:
    case SubscriptionType.PURECONNECT_SUBSCRIPTION:
      return SubscriptionName.PURECONNECT_SUBSCRIPTION;
    case SubscriptionType.POINTILLIST_LEARNING_SUBSCRIPTION:
    case SubscriptionType.POINTILLIST_SUBSCRIPTION:
      return SubscriptionName.POINTILLIST_SUBSCRIPTION;
    default:
      console.log(`Cannot find subscription name for subscription ${subscription}`);
      return '';
  }
}

/**
 * Return a tuple object pair that returns a subscription id and subscription slug based on a product name
 * @param subscriptionProductName - Subscription product name
 * @returns a tuple object pair with subscription id number and subscription slug string
 */
export function getSubscriptionTuple(subscriptionProductName: string): {
  subscriptionId: number;
  subscriptionSlug: string;
} {
  // NOTE: subscription id = 84 is Genesys Cloud Learning Subscription or Genesys Cloud Learning Subscription
  // NOTE: subscription id = 189 is PureConnect Learning Subscription
  // NOTE: subscription id = 190 is Genesys Engage Learning Subscription or Genesys Engage on-premises Learning Subscription
  // NOTE: subscription id = 497 (Docebo Sandbox and Docebo Production) is Pointillist Learning Subscription
  // NOTE: subscription id = 497 (Docebo Sandbox and Docebo Production) is Pointillist Learning Subscription
  if (subscriptionProductName === Product.GENESYS_CLOUD) {
    return {
      subscriptionId: environment.beyond.genesys_cloud.subscription_id,
      subscriptionSlug: SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION,
    };
  } else if (subscriptionProductName === Product.GENESYS_CLOUD_TRIAL) {
    return {
      subscriptionId: environment.beyond.genesys_cloud_trial.subscription_id,
      subscriptionSlug: SubscriptionType.GENESYS_CLOUD_TRIAL_SUBSCRIPTION,
    };
  } else if (subscriptionProductName === Product.GENESYS_CLOUD_PREMIER_PARTNER) {
    return {
      subscriptionId: environment.beyond.genesys_cloud_partner_premier.subscription_id,
      subscriptionSlug: SubscriptionType.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION,
    };
  } else if (
    subscriptionProductName === Product.GENESYS_ENGAGE ||
    subscriptionProductName === Product.GENESYS_ENGAGE_CLOUD
  ) {
    return {
      subscriptionId: environment.beyond.engage.subscription_id,
      subscriptionSlug: SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION,
    };
  } else if (subscriptionProductName === Product.POINTILLIST) {
    return {
      subscriptionId: environment.beyond.pointillist.subscription_id,
      subscriptionSlug: SubscriptionType.POINTILLIST_SUBSCRIPTION,
    };
  } else if (subscriptionProductName === Product.PURE_CONNECT) {
    return {
      subscriptionId: environment.beyond.pureconnect.subscription_id,
      subscriptionSlug: SubscriptionType.PURECONNECT_SUBSCRIPTION,
    };
  } else {
    console.log(`Missing subscription id and slug cannot be found for ${subscriptionProductName}`);
    return {
      subscriptionId: -1,
      subscriptionSlug: '',
    };
  }
}

/**
 * Return Subscription Display Name by subscription id
 * @param subscriptionId - subscription id number
 * @returns - subscription name string
 */
export function getSubscriptionNameById(subscriptionId: number): string {
  // NOTE: Static ids are read from environment files equivalent subscription ids, since
  // ids are not always the same in Docebo Sandbox and Production.
  switch (subscriptionId) {
    case environment.beyond.genesys_cloud.subscription_id:
      return SubscriptionName.GENESYS_CLOUD_SUBSCRIPTION;
    case environment.beyond.pureconnect.subscription_id:
      return SubscriptionName.PURECONNECT_SUBSCRIPTION;
    case environment.beyond.engage.subscription_id:
      return SubscriptionName.GENESYS_ENGAGE_SUBSCRIPTION;
    case environment.beyond.pointillist.subscription_id:
      return SubscriptionName.POINTILLIST_SUBSCRIPTION;
    case environment.beyond.genesys_cloud_partner_premier.subscription_id:
      return SubscriptionName.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION;
    case environment.beyond.genesys_cloud_trial.subscription_id:
      return SubscriptionName.GENESYS_CLOUD_TRIAL_SUBSCRIPTION;
    default:
      console.log(
        `Missing subscription name cannot be found for subscription id  ${subscriptionId}`
      );
      return '';
  }
}

/**
 * Return Subscription Product Short Name by subscription id
 * @param subscriptionId - subscription id number
 * @returns - subscription product short name
 */
export function getSubscriptionProductShortcutName(subscriptionId: number): string {
  // NOTE: Static ids are read from environment files equivalent subscription ids, since
  // ids are not always the same in Docebo Sandbox and Production.
  switch (subscriptionId) {
    case environment.beyond.genesys_cloud.subscription_id:
      return ProductShortName.GENESYS_CLOUD;
    case environment.beyond.pureconnect.subscription_id:
      return ProductShortName.PURE_CONNECT;
    case environment.beyond.engage.subscription_id:
      return ProductShortName.GENESYS_ENGAGE;
    case environment.beyond.pointillist.subscription_id:
      return ProductShortName.POINTILLIST;
    default:
      console.log(
        `Missing subscription product name cannot be found for subscription id  ${subscriptionId}`
      );
      return '';
  }
}

/**
 * Returns Subscription Slug by subscription id
 * @param subscriptionId - subscription id number
 * @returns - subscription slug string
 */
export function getSubscriptionSlugById(subscriptionId: number): string {
  // NOTE: Static ids are read from environment files equivalent subscription ids, since
  // ids are not always the same in Docebo Sandbox and Production.
  switch (subscriptionId) {
    case environment.beyond.genesys_cloud.subscription_id:
      return SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION;
    case environment.beyond.pureconnect.subscription_id:
      return SubscriptionType.PURECONNECT_SUBSCRIPTION;
    case environment.beyond.engage.subscription_id:
      return SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION;
    case environment.beyond.pointillist.subscription_id:
      return SubscriptionType.POINTILLIST_SUBSCRIPTION;
    case environment.beyond.genesys_cloud_partner_premier.subscription_id:
      return SubscriptionType.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION;
    case environment.beyond.genesys_cloud_trial.subscription_id:
      return SubscriptionType.GENESYS_CLOUD_TRIAL_SUBSCRIPTION;
    default:
      console.log(
        `Missing subscription type name cannot be found for subscription id  ${subscriptionId}`
      );
      return '';
  }
}

/**
 * Return subscription ids from LMS Subscription collection
 * @param subscriptions - array of LMSSubscription objects
 * @returns - array of user subscription ids
 */
export function getSubscriptionIds(subscriptions: LMSSubscription[]): number[] {
  // Build list of active subscription ids form subscription objects
  const subscriptionIds: number[] = [];
  if (subscriptions && subscriptions.length > 0) {
    subscriptions.forEach((subscriptionItem) => {
      if (subscriptionItem) {
        subscriptionIds.push(subscriptionItem.id);
      }
    });
  }
  return subscriptionIds;
}

/**
 * Return an truncated subscription name without the Learning Subscription suffix
 * @param subscriptionName - long subscription name that includes Learning Subscription
 * @returns - truncated subscription name
 */
export function shortenSubscriptionName(subscriptionName: string): string {
  let truncateSubscriptionName = '';
  if (subscriptionName) {
    truncateSubscriptionName = subscriptionName.slice(); // Copy string, don't mutate
    // Create a truncated name that excludes Learning Subscription
    if (subscriptionName) {
      truncateSubscriptionName = truncateSubscriptionName
        .replace('Learning Subscription', '')
        .trim();
    }
  }
  return truncateSubscriptionName;
}

/**
 * Returns Subscription Slug by subscription short name
 * @param subscriptionShortName - subscription short name
 * @returns - subscription slug string
 */
export function getSubscriptionSlugByProductShortName(subscriptionShortName: string): string {
  // subscription shortname should match with the active subscriptions
  switch (subscriptionShortName) {
    case ProductShortName.GENESYS_CLOUD:
      return SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION;
    case ProductShortName.GENESYS_ENGAGE:
      return SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION;
    case ProductShortName.PURE_CONNECT:
      return SubscriptionType.PURECONNECT_SUBSCRIPTION;
    case ProductShortName.POINTILLIST:
      return SubscriptionType.POINTILLIST_SUBSCRIPTION;
    case ProductShortName.GENESYS_CLOUD_PREMIER_PARTNER:
      return SubscriptionType.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION;
    case ProductShortName.GENESYS_CLOUD_TRIAL:
      return SubscriptionType.GENESYS_CLOUD_TRIAL_SUBSCRIPTION;
    default:
      console.log(
        `Missing subscription type name cannot be found for subscription shortcut name ${subscriptionShortName}`
      );
      return '';
  }
}

/**
 * Returns Subscription Slug by subscription name
 * @param productId - SubscriptionProductIds or string that contains the product code
 * @returns - subscription slug string
 */
export function getSubscriptionSlugByProductId(productId: SubscriptionProductIds | string): string {
  switch (productId) {
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_GENESYS_CLOUD:
    case SubscriptionProductIds.PARTNER_GC_PREMIER_LEARNING_SUBSCRIPTION:
    case SubscriptionProductIds.PARTNER_GC_DIGITAL_PLUS_LEARNING_SUBSCRIPTION:
      return SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_ENGAGE:
    case SubscriptionProductIds.PARTNER_TECHNICAL_LEARNING_SUBSCRIPTION_ENGAGE:
      return SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_PURECONNECT:
    case SubscriptionProductIds.PARTNER_TECHNICAL_LEARNING_SUBSCRIPTION_PURECONNECT:
      return SubscriptionType.PURECONNECT_SUBSCRIPTION;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_POINTILLIST:
      return SubscriptionType.POINTILLIST_SUBSCRIPTION;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_AI:
      // NOTE: Redirect to Genesys Cloud since their is an AI Essentials
      return SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION;
    case SubscriptionProductIds.PARTNER_RESELL_GC_TECHNICAL_LEARNING_SUBSCRIPTION:
      return SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_GC_PUREBRIDGE_ATTACH:
      return SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION;
    default:
      console.error(`Missing subscription product id ${productId}`);
      return '';
  }
}

/**
 * Returns Docebo Subscription Name by subscription name
 * @param subscriptionShortName - subscription name
 * @returns - Docebo Group Name string
 */
export function getDoceboSubscriptionNameProductName(
  productId: SubscriptionProductIds | string
): string {
  switch (productId) {
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_GENESYS_CLOUD:
      return SubscriptionName.GENESYS_CLOUD_SUBSCRIPTION;
    case SubscriptionProductIds.PARTNER_GC_PREMIER_LEARNING_SUBSCRIPTION:
      return SubscriptionName.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION;
    case SubscriptionProductIds.PARTNER_GC_DIGITAL_LEARNING_SUBSCRIPTION:
      return SubscriptionName.GENESYS_CLOUD_PARTNER_DIGITAL_SUBSCRIPTION;
    case SubscriptionProductIds.PARTNER_GC_DIGITAL_PLUS_LEARNING_SUBSCRIPTION:
      return SubscriptionName.GENESYS_CLOUD_PARTNER_DIGITAL_PLUS_SUBSCRIPTION;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_ENGAGE:
      return SubscriptionName.GENESYS_ENGAGE_SUBSCRIPTION;
    case SubscriptionProductIds.PARTNER_TECHNICAL_LEARNING_SUBSCRIPTION_ENGAGE:
      return SubscriptionName.GENESYS_ENGAGE_PARTNER_SUBSCRIPTION;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_PURECONNECT:
      return SubscriptionName.PURECONNECT_SUBSCRIPTION;
    case SubscriptionProductIds.PARTNER_TECHNICAL_LEARNING_SUBSCRIPTION_PURECONNECT:
      return SubscriptionName.PURECONNECT_PARTNER_SUBSCRIPTION;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_AI:
      return SubscriptionName.AI_ESSENTIAL_SUBSCRIPTION;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_POINTILLIST:
      return SubscriptionName.POINTILLIST_SUBSCRIPTION;
    case SubscriptionProductIds.AGENT_PLATFORM_AP_TRAINING_FOR_GENESYS_CLOUD:
      return SubscriptionName.AGENT_PLATFORM_SUBSCRIPTION;
    case SubscriptionProductIds.PARTNER_RESELL_AGENT_PLATFORM_AP_SUBSCRIPTION:
      return SubscriptionName.PARTNER_RESELL_AGENT_PLATFORM_AP_SUBSCRIPTION_ENGLISH;
    default:
      console.log(`Missing Docebo subscription name for product id ${productId}`);
      return '';
  }
}

/**
 * Returns Docebo Group Name by subscription name
 * @param productId - SubscriptionProductIds or string that contains the product code
 * @returns - Docebo Group Name string
 */
export function getDoceboGroupNameByProductId(productId: SubscriptionProductIds | string): string {
  switch (productId) {
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_GENESYS_CLOUD:
      return SubscriptionGroupName.GENESYS_CLOUD_USER_GROUP;
    case SubscriptionProductIds.PARTNER_GC_DIGITAL_PLUS_LEARNING_SUBSCRIPTION:
      return SubscriptionGroupName.GENESYS_CLOUD_PARTNER_DIGITAL_PLUS_USER_GROUP;
    case SubscriptionProductIds.PARTNER_GC_PREMIER_LEARNING_SUBSCRIPTION:
      return SubscriptionGroupName.GENESYS_CLOUD_PREMIER_PARTNER_USER_GROUP;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_ENGAGE:
      return SubscriptionGroupName.GENESYS_ENAGE_ON_PREMISES_USER_GROUP;
    case SubscriptionProductIds.PARTNER_TECHNICAL_LEARNING_SUBSCRIPTION_ENGAGE:
      return SubscriptionGroupName.GENESYS_ENAGE_ON_PREMISES_USER_GROUP;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_PURECONNECT:
      return SubscriptionGroupName.PURECONNECT_USER_GROUP;
    case SubscriptionProductIds.PARTNER_TECHNICAL_LEARNING_SUBSCRIPTION_PURECONNECT:
      return SubscriptionGroupName.PURECONNECT_USER_GROUP;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_AI:
      return SubscriptionGroupName.AI_ESSENTIALS_USER_GROUP;
    case SubscriptionProductIds.TECHNICAL_LEARNING_SUBSCRIPTION_POINTILLIST:
      return SubscriptionGroupName.POINTILLIST_USER_GROUP;
    default:
      console.log(`Missing subscription product id ${productId}`);
      return '';
  }
}

/**
 * Generate and return long subscription name to include Learning Subscription suffix
 * @param subscriptionName - long subscription name that includes Learning Subscription
 * @returns - truncated subscription name without the Learning Sub
 */
export function generateLongSubscriptionName(subscriptionName: string): string {
  let fullSubscriptionName = '';
  if (subscriptionName) {
    fullSubscriptionName = subscriptionName.slice();
    if (!fullSubscriptionName.includes('Learning Subscription')) {
      fullSubscriptionName = fullSubscriptionName + ' Learning Subscription';
    }
  }
  return fullSubscriptionName;
}

/**
 * Return boolean if the subscription id is a Genesys Cloud Partner related Subscription.
 * @param subscriptionName - subscription id
 * @returns - boolean
 */
export function isHiddenGenesysCloudCXSubscriptionById(subscriptionId: number): boolean {
  return (
    subscriptionId &&
    (subscriptionId === environment.beyond.genesys_cloud_partner_digital_plus.subscription_id ||
      subscriptionId === environment.beyond.genesys_cloud_partner_digital.subscription_id ||
      subscriptionId === environment.beyond.genesys_cloud_partner_premier.subscription_id ||
      subscriptionId === environment.beyond.genesys_cloud_trial.subscription_id)
  );
}

/**
 * Return boolean if the subscription display name a Genesys Cloud Partner related Subscription.
 * @param subscriptionName - long subscription name that includes Learning Subscription
 * @returns - boolean
 */
export function isHiddenGenesysCloudSubscriptionByDisplayName(subscriptionName: string): boolean {
  return (
    subscriptionName &&
    (subscriptionName === SubscriptionName.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION ||
      subscriptionName === SubscriptionName.GENESYS_CLOUD_TRIAL_SUBSCRIPTION ||
      subscriptionName === SubscriptionName.GENESYS_CLOUD_PARTNER_DIGITAL_SUBSCRIPTION ||
      subscriptionName === SubscriptionName.GENESYS_CLOUD_PARTNER_DIGITAL_PLUS_SUBSCRIPTION)
  );
}

/**
 * Return boolean if the subscription slug a Genesys Cloud Partner related Subscription.
 * @param subscriptionSlug - subscription slug
 * @returns - boolean
 */
export function isHiddenGenesysCloudSubscriptionBySlug(subscriptionSlug: string): boolean {
  return (
    subscriptionSlug &&
    (subscriptionSlug === SubscriptionType.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION ||
      subscriptionSlug === SubscriptionType.GENESYS_CLOUD_TRIAL_SUBSCRIPTION ||
      subscriptionSlug === SubscriptionType.GENESYS_CLOUD_PARTNER_DIGITAL_SUBSCRIPTION ||
      subscriptionSlug === SubscriptionType.GENESYS_CLOUD_PARTNER_DIGITAL_PLUS)
  );
}

/**
 * Returns subscription id by subscription slug
 * @param subscriptionSlug - subscription slug string
 * @returns - subscription id number, or -1 if not found
 */
export function getSubscriptionIdBySlug(subscriptionSlug: string): number {
  switch (subscriptionSlug) {
    case SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION:
      return environment.beyond.genesys_cloud.subscription_id;
    case SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION:
      return environment.beyond.engage.subscription_id;
    case SubscriptionType.PURECONNECT_SUBSCRIPTION:
      return environment.beyond.pureconnect.subscription_id;
    case SubscriptionType.POINTILLIST_SUBSCRIPTION:
      return environment.beyond.pointillist.subscription_id;
    case SubscriptionType.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION:
      return environment.beyond.genesys_cloud_partner_premier.subscription_id;
    case SubscriptionType.GENESYS_CLOUD_TRIAL_SUBSCRIPTION:
      return environment.beyond.genesys_cloud_trial.subscription_id;
    default:
      console.error(
        `getSubscriptionIdBySlug: no subscription id found for slug ${subscriptionSlug}`
      );
      return -1;
  }
}
